import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

/**
 * Phase B-β Slice 14: EntitlementsContext.
 *
 * `/user/profile` レスポンスの `entitlements` フィールド (Slice 14 で backend
 * 拡張) を一次ソースにする。無い場合は `profile.role` / `profile.plan` から
 * 推定する。未認証 (guest) は AuthContext.isAuthenticated=false で判定する。
 *
 * UI 側はこの hook で `isPro` / `isAdmin` / `plan` を直接読み、Pro ロックや
 * 個別 limit 表示に使う。`PaywallModal` の open state は親で持つ前提。
 */

export type PlanName = 'guest' | 'free' | 'pro' | 'admin';

export interface Entitlements {
  plan: PlanName;
  aiScanDailyLimit: number | null;
  watchlistLimit: number | null;
  advancedMetrics: boolean;
  notifications: boolean;
  subscriptionCancelled: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  isPro: boolean;
  isAdmin: boolean;
  loading: boolean;
}

const PLAN_DEFAULTS: Record<PlanName, Omit<Entitlements, 'isPro' | 'isAdmin' | 'loading'>> = {
  guest: {
    plan: 'guest',
    aiScanDailyLimit: 0,
    watchlistLimit: 0,
    advancedMetrics: false,
    notifications: false,
    subscriptionCancelled: false,
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
  },
  free: {
    plan: 'free',
    aiScanDailyLimit: 1,
    watchlistLimit: 20,
    advancedMetrics: false,
    notifications: false,
    subscriptionCancelled: false,
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
  },
  pro: {
    plan: 'pro',
    aiScanDailyLimit: 15,
    watchlistLimit: 200,
    advancedMetrics: true,
    notifications: true,
    subscriptionCancelled: false,
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
  },
  admin: {
    plan: 'admin',
    aiScanDailyLimit: null,
    watchlistLimit: null,
    advancedMetrics: true,
    notifications: true,
    subscriptionCancelled: false,
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
  },
};

function normalizePlan(rawPlan: unknown, rawRole: unknown): PlanName {
  const role = String(rawRole ?? '').trim().toLowerCase();
  if (role === 'admin') return 'admin';
  const plan = String(rawPlan ?? '').trim().toLowerCase();
  if (plan === 'admin') return 'admin';
  if (plan === 'pro') return 'pro';
  if (plan === 'guest') return 'guest';
  return 'free';
}

function fromBackendEntitlements(raw: any): Omit<Entitlements, 'isPro' | 'isAdmin' | 'loading'> | null {
  if (!raw || typeof raw !== 'object') return null;
  const plan = normalizePlan(raw.plan, raw.role);
  // backend のフィールドは snake_case (ai_scan_daily_limit) で返ってくる想定。
  // 念のため camelCase もフォールバックで読む。
  const aiScanDailyLimit =
    raw.ai_scan_daily_limit !== undefined
      ? raw.ai_scan_daily_limit
      : raw.aiScanDailyLimit;
  const watchlistLimit =
    raw.watchlist_limit !== undefined ? raw.watchlist_limit : raw.watchlistLimit;
  const advancedMetrics =
    raw.advanced_metrics !== undefined ? raw.advanced_metrics : raw.advancedMetrics;
  const notifications = raw.notifications;
  const subscriptionCancelled =
    raw.subscription_cancelled !== undefined ? raw.subscription_cancelled : raw.subscriptionCancelled;
  const cancelAtPeriodEnd =
    raw.cancel_at_period_end !== undefined ? raw.cancel_at_period_end : raw.cancelAtPeriodEnd;
  const currentPeriodEnd =
    raw.current_period_end !== undefined ? raw.current_period_end : raw.currentPeriodEnd;
  return {
    plan,
    aiScanDailyLimit:
      typeof aiScanDailyLimit === 'number' ? aiScanDailyLimit : aiScanDailyLimit === null ? null : PLAN_DEFAULTS[plan].aiScanDailyLimit,
    watchlistLimit:
      typeof watchlistLimit === 'number' ? watchlistLimit : watchlistLimit === null ? null : PLAN_DEFAULTS[plan].watchlistLimit,
    advancedMetrics: Boolean(advancedMetrics ?? PLAN_DEFAULTS[plan].advancedMetrics),
    notifications: Boolean(notifications ?? PLAN_DEFAULTS[plan].notifications),
    subscriptionCancelled: Boolean(subscriptionCancelled ?? PLAN_DEFAULTS[plan].subscriptionCancelled),
    cancelAtPeriodEnd: Boolean(cancelAtPeriodEnd ?? PLAN_DEFAULTS[plan].cancelAtPeriodEnd),
    currentPeriodEnd: typeof currentPeriodEnd === 'string' && currentPeriodEnd.trim()
      ? currentPeriodEnd
      : null,
  };
}

const EntitlementsContext = createContext<Entitlements | null>(null);

export function EntitlementsProvider({ children }: { children: ReactNode }) {
  const {
    profile,
    entitlements: authEntitlements,
    isAuthenticated,
    isAuthLoading,
    isProfileLoading,
  } = useAuth();

  const value = useMemo<Entitlements>(() => {
    const loading = isAuthLoading || isProfileLoading;
    // guest 判定: 未認証は問答無用で guest 扱い。
    if (!isAuthenticated) {
      const base = PLAN_DEFAULTS.guest;
      return { ...base, isPro: false, isAdmin: false, loading };
    }

    // backend 拡張: AuthContext.entitlements (= /user/profile レスポンス直下) を
    // 最優先で読む。なければ profile.entitlements、それも無ければ role/plan 推定。
    const rawEnt = authEntitlements ?? (profile as any)?.entitlements;
    const fromBackend = fromBackendEntitlements(rawEnt);
    if (fromBackend) {
      return {
        ...fromBackend,
        isPro: fromBackend.plan === 'pro' || fromBackend.plan === 'admin',
        isAdmin: fromBackend.plan === 'admin',
        loading,
      };
    }

    // フォールバック: profile.role / profile.plan から推定。
    const plan = normalizePlan((profile as any)?.plan, (profile as any)?.role);
    const base = PLAN_DEFAULTS[plan];
    return {
      ...base,
      isPro: plan === 'pro' || plan === 'admin',
      isAdmin: plan === 'admin',
      loading,
    };
  }, [authEntitlements, profile, isAuthenticated, isAuthLoading, isProfileLoading]);

  return (
    <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>
  );
}

export function useEntitlements(): Entitlements {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    throw new Error('useEntitlements must be used inside EntitlementsProvider');
  }
  return ctx;
}
